import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Loader } from '../components/ui/Loader';
import {
  useGetProxyDetailsQuery,
  useGetServer3UpdateOptionsQuery,
  useGetProxyExtendPriceQuery,
  useRenewProxyMutation,
  useUpdateServer3ProxyMutation,
} from '../store/api/proxiesApi';
import { formatDate } from '../utils/dateFormatter';
import { useLanguage } from '../hooks/useLanguage';

export function ProxyDetailsPage() {
  const { proxyId } = useParams<{ proxyId: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRenewDays, setSelectedRenewDays] = useState<number | ''>('');
  const [selectedRenewMonths, setSelectedRenewMonths] = useState<number>(1);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedRegionCode, setSelectedRegionCode] = useState('');
  const [selectedCarrierCode, setSelectedCarrierCode] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const { data, isLoading, error, refetch } = useGetProxyDetailsQuery(proxyId || '');
  const { data: server3OptionsData } = useGetServer3UpdateOptionsQuery(proxyId || '', {
    skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 3 || data?.data.proxy.status === 'expired',
  });
  const { data: extendPriceData, isFetching: isFetchingPrice } = useGetProxyExtendPriceQuery(
    { proxyId: proxyId || '', months: selectedRenewMonths },
    { skip: !proxyId || !data?.data.proxy || data?.data.proxy.serverId !== 1 || data?.data.proxy.status === 'expired' },
  );
  const [renewProxy, { isLoading: isRenewing }] = useRenewProxyMutation();
  const [updateServer3Proxy, { isLoading: isUpdatingServer3 }] = useUpdateServer3ProxyMutation();

  const proxy = data?.data.proxy;
  const server3Options = server3OptionsData?.data;
  const lockDates = server3Options?.lockDates ?? proxy?.lockDates;

  const isLockActive = (lockedUntil?: string | null) =>
    Boolean(lockedUntil && new Date(lockedUntil) > new Date());
  const lockedUntilText = (lockedUntil?: string | null) =>
    lockedUntil ? formatDate(lockedUntil, 'PP h:mm a', currentLanguage) : '';

  useEffect(() => {
    if (!server3Options) return;
    if (selectedRenewDays === '' && server3Options.renewalPeriods.length > 0) {
      setSelectedRenewDays(server3Options.renewalPeriods[0]!);
    }
    if (!selectedCountryCode && server3Options.options.countries.length > 0) {
      setSelectedCountryCode(server3Options.options.countries[0]!.code);
    }
    if (!selectedRegionCode && server3Options.options.states.length > 0) {
      setSelectedRegionCode(server3Options.options.states[0]!.code);
    }
    if (!selectedCarrierCode && server3Options.options.carriers.length > 0) {
      setSelectedCarrierCode(server3Options.options.carriers[0]!.code);
    }
  }, [server3Options, selectedRenewDays, selectedCountryCode, selectedRegionCode, selectedCarrierCode]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError') || t('errors.network'));
    }
  };

  const handleCopyConnectionString = () => {
    if (!proxy) return;
    const connectionString = `${proxy.username}:${proxy.password}@${proxy.ipAddress}:${proxy.port}`;
    handleCopy(connectionString);
  };

  const handleRenew = async () => {
    if (!proxy) return;
    const days = proxy.serverId === 3 && selectedRenewDays !== '' ? selectedRenewDays : undefined;
    const months = proxy.serverId === 1 ? selectedRenewMonths : undefined;
    try {
      await renewProxy({ proxyId: proxy._id, days, months }).unwrap();
      toast.success(t('proxies.details.renewSuccess'));
      await refetch();
    } catch (err) {
      const message = (err as { data?: { message?: string }; message?: string })?.data?.message
        || (err as { message?: string })?.message
        || t('proxies.purchase.error');
      toast.error(message);
    }
  };

  const handleServer3Action = async (
    action: 'change_region' | 'change_country' | 'change_carrier' | 'change_proxy_type' | 'toggle_rotation',
    value?: string
  ) => {
    if (!proxy || proxy.serverId !== 3) return;
    try {
      if (action === 'change_region') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, regionCode: value } }).unwrap();
      } else if (action === 'change_country') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, countryCode: value } }).unwrap();
      } else if (action === 'change_carrier') {
        if (!value) return;
        await updateServer3Proxy({ proxyId: proxy._id, body: { action, carrier: value } }).unwrap();
      } else if (action === 'change_proxy_type') {
        const nextType = proxy.type.toUpperCase().includes('SOCKS') ? 'HTTPS' : 'SOCKS5';
        await updateServer3Proxy({
          proxyId: proxy._id,
          body: { action, proxyType: nextType },
        }).unwrap();
      } else {
        await updateServer3Proxy({ proxyId: proxy._id, body: { action } }).unwrap();
      }

      toast.success(t('proxies.details.updateSuccess'));
      await refetch();
    } catch (err) {
      const message = (err as { data?: { message?: string }; message?: string })?.data?.message
        || (err as { message?: string })?.message
        || t('proxies.purchase.error');
      toast.error(message);
    }
  };

  const requestConfirm = (title: string, message: string, action: () => void) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !proxy) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-5xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('common.retry')}
                </button>
                <Link
                  to="/proxies/list"
                  className="px-6 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.back')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/proxies/list"
              className="text-brand-accent hover:text-brand-accent/80 transition-colors font-body"
            >
              ← {t('common.back')}
            </Link>
            <span
              className={`px-3 py-1 rounded-full text-sm font-body ${
                proxy.status === 'active'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : proxy.status === 'expired'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                    : 'bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50'
              }`}
            >
              {t(`proxies.status.${proxy.status}`)}
            </span>
          </div>

          {/* Connection Details */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
              {t('proxies.details.connectionDetails')}
            </h2>

            <div className="border border-neutral-dark/70 rounded-xl divide-y divide-neutral-dark/70 overflow-hidden">
              {/* IP Address */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.ipAddress')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.ipAddress}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(proxy.ipAddress)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                  aria-label={t('common.copy')}
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                  </svg>
                </button>
              </div>

              {/* Port */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.port')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.port}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(proxy.port.toString())}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                  aria-label={t('common.copy')}
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                  </svg>
                </button>
              </div>

              {/* SOCKS Port */}
              {proxy.port_socks && (
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.portSocks')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.port_socks}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(proxy.port_socks!.toString())}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Username */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.username')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.username}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(proxy.username)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                  aria-label={t('common.copy')}
                  title={t('common.copy')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                  </svg>
                </button>
              </div>

              {/* Password */}
              <div className="flex items-center justify-between gap-3 p-4">
                <div className="flex-1">
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.password')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {showPassword ? proxy.password : '••••••••'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-neutral-dark/70 text-neutral-light hover:bg-neutral-dark transition-colors"
                    aria-label={showPassword ? t('proxies.details.hide') : t('proxies.details.show')}
                    title={showPassword ? t('proxies.details.hide') : t('proxies.details.show')}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-10-7 1.028-3.085 3.983-6.364 7.405-6.9M9.88 9.88A3 3 0 1114.12 14.12M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCopy(proxy.password)}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent/15 text-brand-accent hover:bg-brand-accent/25 transition-colors"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Connection String */}
              <div className="p-4 bg-brand-accent/10">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-neutral-medium font-body">
                    {t('proxies.details.connectionString')}
                  </p>
                  <button
                    type="button"
                    onClick={handleCopyConnectionString}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-accent text-brand-primary hover:shadow-lg hover:shadow-brand-accent/40 transition-all"
                    aria-label={t('common.copy')}
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h7a2 2 0 012 2v7a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v7a2 2 0 002 2h7a2 2 0 002-2v-2M8 7h7a2 2 0 012 2v7" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm font-mono text-neutral-light break-all">
                  {proxy.username}:{proxy.password}@{proxy.ipAddress}:{proxy.port}
                </p>
              </div>
            </div>
          </div>

          {/* Proxy Information */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
              {t('proxies.details.information')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.location')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.location}
                  </p>
                </div>
                {proxy.city && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.city')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-neutral-light">
                      {proxy.city}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.type')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {proxy.type}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.expiresAt')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {formatDate(proxy.expiresAt, 'PP h:mm a', currentLanguage)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.purchasedAt')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-neutral-light">
                    {formatDate(proxy.purchasedAt, 'PP h:mm a', currentLanguage)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-neutral-medium font-body mb-1">
                    {t('proxies.details.price')}
                  </p>
                  <p className="text-lg font-heading font-semibold text-brand-accent">
                    ${Number(proxy.price).toFixed(2)}
                  </p>
                </div>
                {proxy.autoExtend && (
                  <div>
                    <p className="text-sm text-neutral-medium font-body mb-1">
                      {t('proxies.details.autoExtend')}
                    </p>
                    <p className="text-lg font-heading font-semibold text-green-400">
                      {t('common.enabled')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {proxy.status !== 'expired' && (
            <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
              <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
                {t('proxies.details.actions')}
              </h2>

              <div className="space-y-4">
                {proxy.serverId === 1 && (
                  <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                    <label className="text-sm text-neutral-medium font-body">
                      {t('proxies.details.renewPeriod')} ({t('proxies.wizard.months')})
                    </label>
                    <select
                      value={selectedRenewMonths}
                      onChange={(e) => setSelectedRenewMonths(Number(e.target.value))}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    >
                      {[1, 2, 3, 6, 12].map((months) => (
                        <option key={months} value={months}>{months} {t(months === 1 ? 'proxies.wizard.month' : 'proxies.wizard.months')}</option>
                      ))}
                    </select>
                    {extendPriceData?.data && (
                      <p className="text-sm text-brand-accent font-heading font-semibold">
                        {t('proxies.details.price')}: ${extendPriceData.data.price.toFixed(2)}
                      </p>
                    )}
                    {isFetchingPrice && (
                      <p className="text-sm text-neutral-medium">{t('common.loading')}...</p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        requestConfirm(
                          t('proxies.details.renewNow'),
                          `${t('proxies.details.confirmRenew')} (${selectedRenewMonths} ${t(selectedRenewMonths === 1 ? 'proxies.wizard.month' : 'proxies.wizard.months')}${extendPriceData?.data ? ` — $${extendPriceData.data.price.toFixed(2)}` : ''})`,
                          () => { void handleRenew(); }
                        );
                      }}
                      disabled={isRenewing || isFetchingPrice}
                      className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                    </button>
                  </div>
                )}

                {proxy.serverId === 2 && (
                  <button
                    type="button"
                    onClick={() => {
                      requestConfirm(
                        t('proxies.details.renewNow'),
                        t('proxies.details.confirmRenew'),
                        () => { void handleRenew(); }
                      );
                    }}
                    disabled={isRenewing}
                    className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                  </button>
                )}

                {proxy.serverId === 3 && (
                  <>
                    <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                      <label className="text-sm text-neutral-medium font-body">
                        {t('proxies.details.renewPeriod')}
                      </label>
                      <select
                        value={selectedRenewDays}
                        onChange={(e) => setSelectedRenewDays(Number(e.target.value))}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                      >
                        {(server3Options?.renewalPeriods ?? []).map((days) => (
                          <option key={days} value={days}>{days} {t('proxies.wizard.days')}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.renewNow'),
                            t('proxies.details.confirmRenew'),
                            () => { void handleRenew(); }
                          );
                        }}
                        disabled={isRenewing || selectedRenewDays === ''}
                        className="w-full px-4 py-3 rounded-xl bg-brand-accent text-brand-primary font-heading font-semibold hover:shadow-lg hover:shadow-brand-accent/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isRenewing ? t('proxies.details.renewing') : t('proxies.details.renewNow')}
                      </button>
                    </div>

                    {(server3Options?.options.countries.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateCountry')}</label>
                        <select
                          value={selectedCountryCode}
                          onChange={(e) => setSelectedCountryCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.countryUpdateLockedUntil)}
                        >
                          {(server3Options?.options.countries ?? []).map((country) => (
                            <option key={country.code} value={country.code}>{country.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.countryUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.countryUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateCountry'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_country', selectedCountryCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedCountryCode || isLockActive(lockDates?.countryUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    {(server3Options?.options.states.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateRegion')}</label>
                        <select
                          value={selectedRegionCode}
                          onChange={(e) => setSelectedRegionCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.regionUpdateLockedUntil)}
                        >
                          {(server3Options?.options.states ?? []).map((state) => (
                            <option key={state.code} value={state.code}>{state.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.regionUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.regionUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateRegion'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_region', selectedRegionCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedRegionCode || isLockActive(lockDates?.regionUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    {(server3Options?.options.carriers.length ?? 0) > 0 && (
                      <div className="rounded-xl border border-neutral-dark p-4 space-y-3">
                        <label className="text-sm text-neutral-medium font-body">{t('proxies.details.updateCarrier')}</label>
                        <select
                          value={selectedCarrierCode}
                          onChange={(e) => setSelectedCarrierCode(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark bg-brand-primary/50 text-neutral-light font-body focus:outline-none focus:ring-2 focus:ring-brand-accent"
                          disabled={isLockActive(lockDates?.carrierUpdateLockedUntil)}
                        >
                          {(server3Options?.options.carriers ?? []).map((carrier) => (
                            <option key={carrier.code} value={carrier.code}>{carrier.name}</option>
                          ))}
                        </select>
                        {isLockActive(lockDates?.carrierUpdateLockedUntil) && (
                          <p className="text-xs text-amber-400">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.carrierUpdateLockedUntil) })}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            requestConfirm(
                              t('proxies.details.updateCarrier'),
                              t('proxies.details.confirmUpdate'),
                              () => { void handleServer3Action('change_carrier', selectedCarrierCode); }
                            );
                          }}
                          disabled={isUpdatingServer3 || !selectedCarrierCode || isLockActive(lockDates?.carrierUpdateLockedUntil)}
                          className="w-full px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                        >
                          {t('proxies.details.update')}
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.updateProxyTypeOnce'),
                            t('proxies.details.confirmUpdate'),
                            () => { void handleServer3Action('change_proxy_type'); }
                          );
                        }}
                        disabled={isUpdatingServer3 || Boolean(lockDates?.proxyTypeIsLocked)}
                        className="px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                      >
                        {t('proxies.details.updateProxyTypeOnce')}
                        {Boolean(lockDates?.proxyTypeIsLocked) && (
                          <span className="block text-xs text-amber-400 mt-1">
                            {t('proxies.details.locked')}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          requestConfirm(
                            t('proxies.details.toggleRotation'),
                            t('proxies.details.confirmUpdate'),
                            () => { void handleServer3Action('toggle_rotation'); }
                          );
                        }}
                        disabled={isUpdatingServer3 || isLockActive(lockDates?.clientIpUpdateLockedUntil)}
                        className="px-4 py-3 rounded-xl border border-neutral-dark hover:border-brand-accent/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed text-neutral-light font-body"
                      >
                        {t('proxies.details.toggleRotation')}
                        {isLockActive(lockDates?.clientIpUpdateLockedUntil) && (
                          <span className="block text-xs text-amber-400 mt-1">
                            {t('proxies.details.lockedUntil', { date: lockedUntilText(lockDates?.clientIpUpdateLockedUntil) })}
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          confirmAction?.();
          setConfirmOpen(false);
          setConfirmAction(null);
        }}
        title={confirmTitle || t('common.confirm')}
        message={confirmMessage || t('common.confirm')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        type="warning"
        isLoading={isRenewing || isUpdatingServer3}
      />
    </div>
  );
}

